import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react"
import {
  AvailableLanguageKey,
  TextStorage,
  textStorages,
} from "../utils/multilang-text-storage"

interface TextStorageContextType {
  currentLanguage: AvailableLanguageKey
  ts: TextStorage
  setLanguage: (language: AvailableLanguageKey) => void
  availableLanguages: Array<AvailableLanguageKey>
}

const TextStorageContext = createContext<TextStorageContextType | undefined>(
  undefined,
)

interface TextStorageProviderProps {
  children: ReactNode
  defaultLanguage?: AvailableLanguageKey
  localStorageKey?: string
}

export const TextStorageProvider: React.FC<TextStorageProviderProps> = ({
  children,
  defaultLanguage = "hungarian" as AvailableLanguageKey,
  localStorageKey = "language-preference",
}) => {
  const [currentLanguage, setCurrentLanguage] =
    useState<AvailableLanguageKey>(defaultLanguage)

  useEffect(() => {
    try {
      const savedLanguage = localStorage.getItem(localStorageKey)
      if (
        savedLanguage &&
        (savedLanguage === "english" || savedLanguage === "hungarian")
      ) {
        setCurrentLanguage(savedLanguage as AvailableLanguageKey)
      }
    } catch (error) {
      console.warn(
        "Failed to load language preference from localStorage:",
        error,
      )
    }
  }, [localStorageKey])

  const currentTextStorage: TextStorage = textStorages[currentLanguage]
  const availableLanguages: Array<AvailableLanguageKey> = Object.keys(
    textStorages,
  ) as Array<AvailableLanguageKey>

  const setLanguage = (language: AvailableLanguageKey) => {
    setCurrentLanguage(language)
    try {
      localStorage.setItem(localStorageKey, language)
    } catch (error) {
      console.warn("Failed to save language preference to localStorage:", error)
    }
  }

  const value: TextStorageContextType = {
    currentLanguage,
    ts: currentTextStorage,
    setLanguage,
    availableLanguages,
  }

  return (
    <TextStorageContext.Provider value={value}>
      {children}
    </TextStorageContext.Provider>
  )
}

export const useTextStorage = (): TextStorageContextType => {
  const context = useContext(TextStorageContext)
  if (context === undefined) {
    throw new Error("useTextStorage must be used within a TextStorageProvider")
  }
  return context
}
